import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { ConfirmAction } from '@/components/confirm-action'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ApiError, CODE } from '@/lib/api'
import {
  useApproveReward,
  useRejectReward,
  useRejectTask,
  useRewardApprovals,
  useVerifications,
  useVerifyTask,
  type RewardApprovalRow,
  type VerificationRow,
} from '@/features/rewards/api'
import { localDate } from '@/lib/datetime'

const PAGE_SIZE = 20

/** Which confirm dialog is open, and on which row. */
type PendingDecision =
  | { stage: 'verify'; action: 'verify' | 'reject'; row: VerificationRow }
  | { stage: 'approval'; action: 'approve' | 'reject'; row: RewardApprovalRow }

export function RewardsPage() {
  const [verifyPage, setVerifyPage] = useState(1)
  const [approvalPage, setApprovalPage] = useState(1)
  const [pending, setPending] = useState<PendingDecision | null>(null)

  /**
   * Selected approval rows, by `quest_member_progress.id` as a string.
   *
   * Cleared whenever the page changes, which keeps selection inside the rows
   * currently loaded. That is what lets the total below be accurate: the point
   * values come from the fetched page, so a selection spanning pages we no
   * longer hold would under-report what the operator is about to grant.
   */
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkResult, setBulkResult] = useState<string | null>(null)

  // Both run regardless of the visible tab, so the counts on the tabs are real.
  const verifications = useVerifications({ page: verifyPage, pageSize: PAGE_SIZE })
  const approvals = useRewardApprovals({ page: approvalPage, pageSize: PAGE_SIZE })

  const verifyTask = useVerifyTask()
  const rejectTask = useRejectTask()
  const approveReward = useApproveReward()
  const rejectReward = useRejectReward()

  const verifyColumns = useMemo<ColumnDef<VerificationRow, unknown>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Member',
        cell: ({ row }) => <span className="font-semibold">{row.original.display_name}</span>,
      },
      {
        // Its own column rather than a subtitle under the name, so it can be
        // sorted and copied cleanly. Monospaced because these get compared
        // character by character against other systems.
        accessorKey: 'user_id',
        header: 'UID',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-faint">{row.original.user_id}</span>
        ),
      },
      { accessorKey: 'quest_name', header: 'Task group' },
      { accessorKey: 'task_title', header: 'Task awaiting check' },
      {
        // What the operator actually needs to do the check. There is no X API, so
        // verifying a follow means a person opening x.com and looking — and that
        // needs the handle here, not one click away on the member detail. Mono for
        // the same reason as UID above: these get compared character by character
        // against another system.
        id: 'handles',
        header: 'Member handles',
        enableSorting: false,
        cell: ({ row }) => {
          const { twitter_handle, biconomy_uid } = row.original
          if (!twitter_handle && !biconomy_uid) {
            return <span className="text-faint text-xs">—</span>
          }
          return (
            <div className="flex flex-col gap-0.5 font-mono text-xs whitespace-nowrap">
              {twitter_handle && (
                <a
                  href={`https://x.com/${twitter_handle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  @{twitter_handle}
                </a>
              )}
              {biconomy_uid && <span className="text-faint">{biconomy_uid}</span>}
            </div>
          )
        },
      },
      {
        accessorKey: 'submitted_at',
        header: 'Submitted',
        cell: ({ row }) => localDate(row.original.submitted_at),
      },
      {
        id: 'state',
        header: 'State',
        enableSorting: false,
        cell: () => <Badge variant="warning">Submitted</Badge>,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2 whitespace-nowrap">
            <Button
              size="sm"
              onClick={() => setPending({ stage: 'verify', action: 'verify', row: row.original })}
            >
              Verify
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPending({ stage: 'verify', action: 'reject', row: row.original })}
            >
              Reject
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  const approvalColumns = useMemo<ColumnDef<RewardApprovalRow, unknown>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Member',
        cell: ({ row }) => <span className="font-semibold">{row.original.display_name}</span>,
      },
      {
        // Its own column rather than a subtitle under the name, so it can be
        // sorted and copied cleanly. Monospaced because these get compared
        // character by character against other systems.
        accessorKey: 'user_id',
        header: 'UID',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-faint">{row.original.user_id}</span>
        ),
      },
      { accessorKey: 'quest_name', header: 'Task group' },
      {
        accessorKey: 'reward_points',
        header: 'Reward',
        cell: ({ row }) => (
          <span className="font-semibold">+{row.original.reward_points.toLocaleString()}</span>
        ),
      },
      {
        accessorKey: 'completed_at',
        header: 'Completed',
        cell: ({ row }) => localDate(row.original.completed_at),
      },
      {
        id: 'state',
        header: 'State',
        enableSorting: false,
        cell: () => <Badge variant="warning">Awaiting reward</Badge>,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2 whitespace-nowrap">
            <Button
              size="sm"
              onClick={() =>
                setPending({ stage: 'approval', action: 'approve', row: row.original })
              }
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPending({ stage: 'approval', action: 'reject', row: row.original })}
            >
              Reject
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  const approvalRows = approvals.data?.list ?? []
  const selectedRows = approvalRows.filter((row) => selectedIds.includes(String(row.id)))
  const selectedPoints = selectedRows.reduce((sum, row) => sum + row.reward_points, 0)

  /**
   * Approves each selected row through the same audited endpoint the single-row
   * button uses, one at a time.
   *
   * Sequential rather than parallel, and per-row rather than one batch call, for
   * the same reason: any individual row can come back `2001` because another
   * operator got to it first, and that is the normal case here. A batch endpoint
   * would first need someone to decide whether the whole set fails when one row
   * does; looping keeps each row's outcome its own and reports honestly.
   */
  async function approveSelected(note: string) {
    let approved = 0
    let alreadyActioned = 0
    const failures: string[] = []

    for (const row of selectedRows) {
      try {
        await approveReward.mutateAsync({ id: row.id, note: note || undefined })
        approved += 1
      } catch (thrown) {
        if (thrown instanceof ApiError && thrown.isInvalidState) {
          alreadyActioned += 1
        } else {
          failures.push(row.display_name)
        }
      }
    }

    setSelectedIds([])
    void approvals.refetch()

    // Nothing landed and the only reason was other operators — that is exactly
    // what ConfirmAction's 2001 branch says, so let it say it.
    if (approved === 0 && alreadyActioned > 0 && failures.length === 0) {
      throw new ApiError(200, CODE.INVALID_STATE, 'already actioned')
    }

    const parts = [`${approved} approved`]
    if (alreadyActioned > 0) parts.push(`${alreadyActioned} already actioned by someone else`)
    if (failures.length > 0) parts.push(`${failures.length} failed (${failures.join(', ')})`)
    setBulkResult(parts.join(' · '))
  }

  return (
    <>
      <PageHeader title="Rewards" />

      <Notice>
        <b>Two manual gates in Phase 1.</b> ① <b>Task verification</b> — a member finished a
        manual-verify task that needs a staff check before it counts. ② <b>Reward approval</b> — once
        every task is done, the whole-group points are released, but only for groups set to{' '}
        <i>Requires approval</i>. Verifying the last outstanding task completes the group and moves
        the member to reward approval, or auto-credits if the group is Auto-credit.
      </Notice>

      <Card>
        <Tabs defaultValue="verify">
          <TabsList>
            <TabsTrigger value="verify">
              Task verification
              <QueueCount value={verifications.data?.total} />
            </TabsTrigger>
            <TabsTrigger value="approval">
              Reward approval
              <QueueCount value={approvals.data?.total} />
            </TabsTrigger>
          </TabsList>

          <p className="mt-4 rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground">
            Member completes tasks → ① Task verification → group complete → ② Reward approval →
            points granted
          </p>

          <TabsContent value="verify" className="pt-4">
            <CardDescription className="mb-3">
              Manual-verify tasks a member has completed, awaiting a staff check.{' '}
              <b>Verify</b> the last outstanding task and the group completes; <b>Reject</b>{' '}
              withholds it. Every action is audited.
            </CardDescription>
            <DataTable
              columns={verifyColumns}
              query={verifications}
              pagination={{ page: verifyPage, onPageChange: setVerifyPage }}
              emptyMessage="Nothing to verify. Auto-verifiable tasks never appear here."
            />
          </TabsContent>

          <TabsContent value="approval" className="pt-4">
            <CardDescription className="mb-3">
              Groups that are fully complete and set to <b>Requires approval</b>. Approving grants
              the whole-group points. <b>Single-person approval</b> — the audit log is the control.
            </CardDescription>

            {bulkResult && (
              <div className="mb-3 flex items-start justify-between gap-3 rounded-lg bg-secondary px-3 py-2 text-xs">
                <span>{bulkResult}</span>
                <button
                  type="button"
                  className="shrink-0 font-semibold text-muted-foreground hover:text-foreground"
                  onClick={() => setBulkResult(null)}
                >
                  Dismiss
                </button>
              </div>
            )}

            {selectedRows.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2">
                <span className="text-sm">
                  <b>{selectedRows.length} selected</b>
                  <span className="text-muted-foreground">
                    {' '}
                    · {selectedPoints.toLocaleString()} points will be granted
                  </span>
                </span>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setBulkOpen(true)}>
                    Approve {selectedRows.length} selected
                  </Button>
                </div>
              </div>
            )}

            <DataTable
              columns={approvalColumns}
              query={approvals}
              pagination={{
                page: approvalPage,
                onPageChange: (page) => {
                  setApprovalPage(page)
                  setSelectedIds([])
                },
              }}
              selection={{
                rowId: (row) => String(row.id),
                selected: selectedIds,
                onChange: setSelectedIds,
              }}
              emptyMessage="All caught up — no completed groups are waiting."
            />
          </TabsContent>
        </Tabs>
      </Card>

      {bulkOpen && (
        <ConfirmAction
          open
          onOpenChange={setBulkOpen}
          title={`Approve ${selectedRows.length} rewards?`}
          description={
            <>
              <b>{selectedPoints.toLocaleString()} points</b> will be credited across{' '}
              <b>{selectedRows.length} members</b>. Each one writes to the points ledger and
              cannot be undone from the console. Any row another operator has already actioned is
              skipped and reported.
            </>
          }
          confirmLabel={`Approve ${selectedRows.length}`}
          note={{ placeholder: 'Recorded against every approval in this batch.' }}
          onConfirm={approveSelected}
          onConflict={() => void approvals.refetch()}
        />
      )}

      {pending && (
        <ConfirmAction
          open
          onOpenChange={(open) => !open && setPending(null)}
          title={confirmTitle(pending)}
          description={confirmDescription(pending)}
          confirmLabel={confirmLabel(pending)}
          destructive={pending.action === 'reject'}
          note={{
            placeholder:
              pending.action === 'reject'
                ? 'Why is this being rejected? Recorded in the audit log.'
                : 'Anything worth recording alongside this decision.',
          }}
          onConfirm={(note) => {
            const options = { note: note || undefined }
            if (pending.stage === 'verify') {
              const id = pending.row.member_task_id
              return pending.action === 'verify'
                ? verifyTask.mutateAsync({ id, ...options })
                : rejectTask.mutateAsync({ id, ...options })
            }
            const id = pending.row.id
            return pending.action === 'approve'
              ? approveReward.mutateAsync({ id, ...options })
              : rejectReward.mutateAsync({ id, ...options })
          }}
          onConflict={() => {
            void verifications.refetch()
            void approvals.refetch()
          }}
        />
      )}
    </>
  )
}

function QueueCount({ value }: { value?: number }) {
  if (value === undefined) return null
  return (
    <span className="ml-1.5 rounded-full bg-primary-soft px-1.5 text-[11px] font-bold text-accent-foreground">
      {value}
    </span>
  )
}

function confirmTitle(pending: PendingDecision): string {
  if (pending.stage === 'verify') {
    return pending.action === 'verify' ? 'Verify this task?' : 'Reject this task?'
  }
  return pending.action === 'approve' ? 'Approve this reward?' : 'Reject this reward?'
}

function confirmLabel(pending: PendingDecision): string {
  if (pending.stage === 'verify') return pending.action === 'verify' ? 'Verify' : 'Reject'
  return pending.action === 'approve' ? 'Approve' : 'Reject'
}

function confirmDescription(pending: PendingDecision) {
  if (pending.stage === 'verify') {
    const { display_name, task_title } = pending.row
    return pending.action === 'verify' ? (
      <>
        “{task_title}” will count as complete for <b>{display_name}</b>. If it is the last
        outstanding task, the group completes — its reward is either released immediately or
        sent for approval, depending on how the group is set up.
      </>
    ) : (
      <>
        “{task_title}” will be withheld for <b>{display_name}</b>. They keep any other verified
        tasks in the group.
      </>
    )
  }

  const { display_name, reward_points, quest_name } = pending.row
  return pending.action === 'approve' ? (
    <>
      <b>{reward_points.toLocaleString()} points</b> will be credited to{' '}
      <b>{display_name}</b> for “{quest_name}”. This writes to the points ledger and cannot be
      undone from the console.
    </>
  ) : (
    <>
      <b>{display_name}</b> will not be paid for “{quest_name}”. No ledger entry is written.
    </>
  )
}
